import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Page } from "../components/FG/Page";
import { EditorialLanding } from "../components/Landing/EditorialLanding";
import { LandingSubNav } from "../components/Landing/LandingSubNav";

/**
 * Landing "1a — The Editorial" — the pre-app entry screen.
 *
 * A long-form editorial landing (hero → sweepstakes → problem → how-it-works →
 * what's-in-a-box → who-it's-for → FAQ → final CTA). The SPA mounts inside the
 * Shopify storefront theme, which supplies the site header + footer. The
 * sub-nav slot holds a `<LandingSubNav>` (section anchors + a "Build my
 * fueling plan" CTA) instead of the app tabs.
 *
 * Full-bleed: section backgrounds flow 100% width; each band caps its content to
 * `.fg-page-width`'s wrapper (--fg-page-max-width + gutter) so it lines up with
 * the sub-nav and the surrounding storefront.
 *
 * `showSweepstakes` gates both the sweepstakes band AND its "Win free fuel"
 * sub-nav anchor, so the campaign toggles as one. Sweepstakes copy/dates are
 * placeholders — swap for real content.
 */
export function LandingScreen() {
  const showSweepstakes = false;
  const { hash } = useLocation();

  // Smooth-scroll for the sub-nav section anchors, scoped to the landing: the
  // document is the scroll container, so we toggle the behaviour on <html> while
  // this screen is mounted. The CSS rule is guarded by prefers-reduced-motion.
  useEffect(() => {
    document.documentElement.classList.add("fg-smooth-scroll");
    return () => document.documentElement.classList.remove("fg-smooth-scroll");
  }, []);

  // Warm the lazy route chunks the primary CTA funnels into (W-13): the wizard
  // apply lands on /my-fuel, and "just training" paths land on the planner.
  // Deferred to idle so it never competes with the landing's own paint; Vite
  // dedupes these against router.tsx's lazy() imports, so each chunk loads once.
  useEffect(() => {
    const warm = () => {
      void import("./MyFuel");
      void import("./MyTraining");
    };
    if (typeof window.requestIdleCallback === "function") {
      const idle = window.requestIdleCallback(warm);
      return () => window.cancelIdleCallback(idle);
    }
    // Safari has no requestIdleCallback — a short timeout is close enough.
    const timer = window.setTimeout(warm, 1500);
    return () => window.clearTimeout(timer);
  }, []);

  // Deep links from `/faq` (and bookmarks) arrive with a section hash — scroll
  // after mount so they land below the global ScrollToTop reset. Two quirks:
  // scroll on the NEXT frame (a same-commit smooth scroll gets cancelled by the
  // ScrollToTop reset, leaving the page at the top), jump instantly (a 10k-px
  // smooth animation on arrival is disorienting and easy to interrupt), and
  // re-run once after images settle so the section stays under the viewport.
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const scrollToSection = () =>
      document.getElementById(id)?.scrollIntoView({ behavior: "instant", block: "start" });
    const raf = requestAnimationFrame(scrollToSection);
    const settle = window.setTimeout(scrollToSection, 300);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(settle);
    };
  }, [hash]);

  return (
    <Page bg="var(--fg-app-bg)" subnav={<LandingSubNav showSweepstakes={showSweepstakes} />}>
      <EditorialLanding showSweepstakes={showSweepstakes} />
    </Page>
  );
}
